/* Quote request drawer — the customer-facing half of the "Request a quote"
   flow for 2.5"+ backflow devices that can't self-checkout.

   Products gated by the parts finder (assets/repair-parts-finder.js) and the
   product page (sections/main-product.liquid) dispatch their items here; this
   element keeps a quote list in localStorage, shows a floating launcher +
   drawer, and POSTs the list + the customer's contact info to the app
   (/api/storefront/quote-request). The app pre-fills a draft estimate and
   emails staff, who confirm availability and send the real estimate.

   Decoupled by design: callers only emit a `quote:add` event (or carry a
   [data-add-to-quote] attribute) — they never touch storage, markup, or the
   network call. */

if (!customElements.get('quote-request')) {
  const STORAGE_KEY = 'bfd_quote_items_v1';
  const REQUEST_ID_KEY = 'bfd_quote_request_id_v1';

  class QuoteRequest extends HTMLElement {
    constructor() {
      super();
      this.apiBase = (this.dataset.apiBase || 'https://app.thebackflowdepot.com').replace(/\/+$/, '');
      this.items = this.readItems();
      this.open = false;
      this.submitting = false;
      // Form values are kept in component state so a re-render (triggered by a
      // cart edit) never wipes what the customer has typed. The optional
      // shipping section starts collapsed.
      this.form = {
        name: '', company: '', email: '', phone: '', note: '',
        address: '', city: '', state: '', zip: '', country: '',
      };
      this.addressOpen = false;
    }

    connectedCallback() {
      this.render();
      // Items added from the finder / product page.
      document.addEventListener('quote:add', (event) => {
        if (event.detail && event.detail.variantId) this.addItem(event.detail);
      });
      // Product-page buttons ([data-add-to-quote]) read their selected variant
      // from the surrounding product form. (Finder cards instead dispatch
      // quote:add directly with their own variant id, so they don't match
      // this selector.)
      document.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-add-to-quote]');
        if (!btn) return;
        event.preventDefault();
        this.addFromProductForm(btn);
      });
    }

    /* ---- Storage ---- */

    readItems() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    persist() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch {
        /* storage full / disabled — keep the in-memory list working */
      }
      // Let the product page (sticky bar / "in your quote" line) refresh its count.
      document.dispatchEvent(new CustomEvent('quote:changed'));
    }

    /** Stable idempotency key for the current draft request, so a retried
        submit converges on one draft instead of duplicating it. Rotated after
        a successful submit. */
    requestId() {
      try {
        let id = localStorage.getItem(REQUEST_ID_KEY);
        if (!id) {
          id = this.uuid();
          localStorage.setItem(REQUEST_ID_KEY, id);
        }
        return id;
      } catch {
        return this.uuid();
      }
    }

    rotateRequestId() {
      try {
        localStorage.removeItem(REQUEST_ID_KEY);
      } catch {
        /* ignore */
      }
    }

    uuid() {
      if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
      return 'q-' + Date.now() + '-' + Math.round(Math.random() * 1e9);
    }

    /* ---- Mutations ---- */

    addItem(detail) {
      const variantId = String(detail.variantId);
      const qty = Math.max(1, Math.floor(Number(detail.quantity) || 1));
      const existing = this.items.find((it) => it.variantId === variantId);
      if (existing) {
        existing.quantity += qty;
      } else {
        this.items.push({
          variantId,
          title: detail.title || 'Item',
          sku: detail.sku || null,
          image: detail.image || null,
          handle: detail.handle || null,
          quantity: qty,
        });
      }
      // The cart changed, so this is a different request — rotate the
      // idempotency key. A stable key only survives an *unchanged* cart, so a
      // true double-submit dedupes onto one draft, but editing the cart and
      // resubmitting creates a fresh request instead of dropping the edits.
      this.rotateRequestId();
      this.persist();
      this.open = true;
      this.render();
    }

    addFromProductForm(btn) {
      const form =
        btn.closest('form.js-product-form') ||
        document.querySelector('form.js-product-form-main') ||
        document.querySelector('form.js-product-form');
      const idInput = form && form.querySelector('[name="id"]');
      const variantId = idInput && idInput.value;
      if (!variantId) return;
      const qtyInput = form && form.querySelector('[name="quantity"]');
      const quantity = qtyInput ? Number(qtyInput.value) || 1 : 1;

      // Resolve a friendly title + sku from the emitted variants map.
      let variantTitle = null;
      let sku = null;
      const mapEl = (form && form.querySelector('[data-quote-variants]')) || document.querySelector('[data-quote-variants]');
      if (mapEl) {
        try {
          const variants = JSON.parse(mapEl.textContent);
          const match = variants.find((v) => String(v.id) === String(variantId));
          if (match) {
            sku = match.sku || null;
            if (match.title && match.title.toLowerCase() !== 'default title') variantTitle = match.title;
          }
        } catch {
          /* ignore malformed map */
        }
      }
      const baseTitle = btn.dataset.productTitle || document.title;
      this.addItem({
        variantId,
        title: variantTitle ? `${baseTitle} — ${variantTitle}` : baseTitle,
        sku,
        image: btn.dataset.productImage || null,
        handle: btn.dataset.productHandle || null,
        quantity,
      });
    }

    setQuantity(variantId, quantity) {
      const item = this.items.find((it) => it.variantId === variantId);
      if (!item) return;
      const q = Math.floor(Number(quantity) || 0);
      if (q <= 0) {
        this.items = this.items.filter((it) => it.variantId !== variantId);
      } else {
        // Cap at 99 to match the app's per-line quantity limit.
        item.quantity = Math.min(99, q);
      }
      this.rotateRequestId();
      this.persist();
      this.render();
    }

    removeItem(variantId) {
      this.items = this.items.filter((it) => it.variantId !== variantId);
      this.rotateRequestId();
      this.persist();
      this.render();
    }

    clear() {
      this.items = [];
      this.persist();
      this.render();
    }

    /* ---- Submit ---- */

    async submit(formData) {
      if (this.submitting) return;
      if (this.items.length === 0) return;
      this.submitting = true;
      this.render();

      const payload = {
        requestId: this.requestId(),
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: formData.phone,
        note: formData.note,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        country: formData.country,
        // An `inquiry:` item (e.g. a freeze bag for this device) is not a
        // Shopify product, so its title is what the app puts on the estimate.
        items: this.items.map((it) => (String(it.variantId).startsWith('inquiry:')
          ? { variantId: it.variantId, quantity: it.quantity, title: it.title }
          : { variantId: it.variantId, quantity: it.quantity })),
      };

      try {
        const res = await fetch(`${this.apiBase}/api/storefront/quote-request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          this.error = (data && data.error) || 'Something went wrong. Please try again or call us.';
          this.submitting = false;
          this.render();
          return;
        }
        // Success: clear the list, rotate the idempotency key so the next
        // quote is a fresh request, and show the confirmation.
        this.clearForSuccess();
      } catch {
        this.error = 'Could not reach the server. Please check your connection and try again.';
        this.submitting = false;
        this.render();
      }
    }

    clearForSuccess() {
      this.items = [];
      this.persist();
      this.rotateRequestId();
      this.submitting = false;
      this.error = null;
      this.submitted = true;
      this.form = {
        name: '', company: '', email: '', phone: '', note: '',
        address: '', city: '', state: '', zip: '', country: '',
      };
      this.addressOpen = false;
      this.render();
    }

    /* ---- Rendering ---- */

    get count() {
      return this.items.reduce((sum, it) => sum + it.quantity, 0);
    }

    escape(value) {
      return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;',
      }[c]));
    }

    render() {
      const count = this.count;
      const launcher = count > 0 || this.open
        ? `<button type="button" class="quote-launcher" data-quote-toggle aria-label="Open quote request">
             <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg>
             <span>Quote</span>
             ${count > 0 ? `<span class="quote-launcher__count">${count}</span>` : ''}
           </button>`
        : '';

      this.innerHTML = `
        ${launcher}
        <div class="quote-drawer${this.open ? ' is-open' : ''}" role="dialog" aria-modal="true" aria-label="Quote request" ${this.open ? '' : 'hidden'}>
          <div class="quote-drawer__backdrop" data-quote-close></div>
          <div class="quote-drawer__panel">
            <div class="quote-drawer__header">
              <h2 class="quote-drawer__title">Request a quote</h2>
              <button type="button" class="quote-drawer__close" data-quote-close aria-label="Close">&times;</button>
            </div>
            <div class="quote-drawer__body">
              ${this.submitted ? this.renderSuccess() : this.renderContent()}
            </div>
          </div>
        </div>
      `;

      this.bind();
    }

    renderSuccess() {
      return `
        <div class="quote-success">
          <div class="quote-success__icon" aria-hidden="true">✓</div>
          <h3>Request sent!</h3>
          <p>Thanks — we've received your quote request and emailed you a confirmation. Our team will confirm availability and pricing, then send you an estimate to review.</p>
          <button type="button" class="btn btn--primary w-full" data-quote-close>Done</button>
        </div>
      `;
    }

    renderContent() {
      if (this.items.length === 0) {
        return `<p class="quote-empty">Your quote request is empty. Browse larger backflow devices and tap “Add to quote”.</p>`;
      }
      const rows = this.items.map((it) => `
        <li class="quote-item">
          ${it.image ? `<img class="quote-item__img" src="${this.escape(it.image)}" alt="" loading="lazy">` : '<div class="quote-item__img quote-item__img--ph"></div>'}
          <div class="quote-item__info">
            <p class="quote-item__title">${it.handle ? `<a href="/products/${this.escape(it.handle)}">${this.escape(it.title)}</a>` : this.escape(it.title)}</p>
            ${it.sku ? `<p class="quote-item__sku">SKU: ${this.escape(it.sku)}</p>` : ''}
          </div>
          <div class="quote-item__qty">
            <button type="button" class="quote-qty-btn" data-quote-dec="${this.escape(it.variantId)}" aria-label="Decrease quantity">−</button>
            <input type="number" min="1" max="99" value="${it.quantity}" class="quote-qty-input" data-quote-qty="${this.escape(it.variantId)}" aria-label="Quantity">
            <button type="button" class="quote-qty-btn" data-quote-inc="${this.escape(it.variantId)}" aria-label="Increase quantity">+</button>
          </div>
          <button type="button" class="quote-item__remove" data-quote-remove="${this.escape(it.variantId)}" aria-label="Remove">&times;</button>
        </li>
      `).join('');

      const v = (name) => this.escape(this.form[name] || '');
      return `
        <ul class="quote-list">${rows}</ul>
        ${this.error ? `<p class="quote-form__error" role="alert">${this.escape(this.error)}</p>` : ''}
        <form class="quote-form" data-quote-form novalidate>
          <p class="quote-form__intro">These items may be on backorder, special-ordered from the manufacturer, or need special shipping — so instead of checking out, send us a request and we'll confirm availability, shipping, and pricing, then email you an estimate to approve.</p>
          <label class="quote-form__field">
            <span>Name<span class="quote-req">*</span></span>
            <input type="text" name="name" required autocomplete="name" value="${v('name')}">
          </label>
          <label class="quote-form__field">
            <span>Company</span>
            <input type="text" name="company" autocomplete="organization" value="${v('company')}">
          </label>
          <label class="quote-form__field">
            <span>Email<span class="quote-req">*</span></span>
            <input type="email" name="email" required autocomplete="email" value="${v('email')}">
          </label>
          <label class="quote-form__field">
            <span>Phone</span>
            <input type="tel" name="phone" autocomplete="tel" value="${v('phone')}">
          </label>
          <label class="quote-form__field">
            <span>ZIP code<span class="quote-req">*</span></span>
            <input type="text" name="zip" required autocomplete="postal-code" inputmode="numeric" maxlength="10" aria-describedby="quote-zip-help" value="${v('zip')}">
            <small class="quote-form__help" id="quote-zip-help">We need your ZIP to calculate shipping for special parts.</small>
          </label>

          <div class="quote-address">
            <button type="button" class="quote-address__toggle" data-quote-address-toggle aria-expanded="${this.addressOpen ? 'true' : 'false'}">
              <span class="quote-address__chevron" aria-hidden="true">${this.addressOpen ? '−' : '+'}</span>
              ${this.addressOpen ? 'Shipping address' : 'Add the rest of your shipping address (optional)'}
            </button>
            <p class="quote-address__hint">Want your estimate to include shipping &amp; taxes? Add your address and we'll work out the full delivered price for you — otherwise we'll quote the parts and sort the rest out together.</p>
            <div class="quote-address__fields"${this.addressOpen ? '' : ' hidden'}>
              <label class="quote-form__field">
                <span>Street address</span>
                <input type="text" name="address" autocomplete="address-line1" value="${v('address')}">
              </label>
              <div class="quote-address__row">
                <label class="quote-form__field">
                  <span>City</span>
                  <input type="text" name="city" autocomplete="address-level2" value="${v('city')}">
                </label>
                <label class="quote-form__field quote-form__field--sm">
                  <span>State</span>
                  <input type="text" name="state" autocomplete="address-level1" value="${v('state')}">
                </label>
              </div>
              <label class="quote-form__field">
                <span>Country</span>
                <input type="text" name="country" autocomplete="country-name" placeholder="United States" value="${v('country')}">
              </label>
            </div>
          </div>

          <label class="quote-form__field">
            <span>Note (optional)</span>
            <textarea name="note" rows="3" placeholder="Anything we should know?">${v('note')}</textarea>
          </label>
          <button type="submit" class="btn btn--primary w-full" ${this.submitting ? 'disabled' : ''}>
            ${this.submitting ? 'Sending…' : 'Submit quote request'}
          </button>
        </form>
      `;
    }

    bind() {
      this.querySelector('[data-quote-toggle]')?.addEventListener('click', () => {
        this.open = !this.open;
        // Re-opening after a success should start a clean request.
        if (this.open && this.submitted) this.submitted = false;
        this.render();
      });
      this.querySelectorAll('[data-quote-close]').forEach((el) =>
        el.addEventListener('click', () => {
          this.open = false;
          this.submitted = false;
          this.render();
        })
      );
      this.querySelectorAll('[data-quote-remove]').forEach((el) =>
        el.addEventListener('click', () => this.removeItem(el.dataset.quoteRemove))
      );
      this.querySelectorAll('[data-quote-inc]').forEach((el) =>
        el.addEventListener('click', () => {
          const item = this.items.find((it) => it.variantId === el.dataset.quoteInc);
          if (item) this.setQuantity(item.variantId, item.quantity + 1);
        })
      );
      this.querySelectorAll('[data-quote-dec]').forEach((el) =>
        el.addEventListener('click', () => {
          const item = this.items.find((it) => it.variantId === el.dataset.quoteDec);
          if (item) this.setQuantity(item.variantId, item.quantity - 1);
        })
      );
      this.querySelectorAll('[data-quote-qty]').forEach((el) =>
        el.addEventListener('change', () => this.setQuantity(el.dataset.quoteQty, el.value))
      );

      // Collapsible shipping-address section.
      this.querySelector('[data-quote-address-toggle]')?.addEventListener('click', () => {
        this.addressOpen = !this.addressOpen;
        this.render();
      });

      const form = this.querySelector('[data-quote-form]');
      // Keep form state in sync on every keystroke so a cart edit (which
      // re-renders the drawer) never wipes what the customer typed.
      form?.addEventListener('input', (event) => {
        const el = event.target;
        if (el && el.name && Object.prototype.hasOwnProperty.call(this.form, el.name)) {
          this.form[el.name] = el.value;
        }
      });
      form?.addEventListener('submit', (event) => {
        event.preventDefault();
        const fd = new FormData(form);
        const get = (k) => (fd.get(k) || '').toString().trim();
        const name = get('name');
        const email = get('email');
        if (!name) {
          this.error = 'Please enter your name.';
          this.render();
          return;
        }
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          this.error = 'Please enter a valid email address.';
          this.render();
          return;
        }
        // ZIP is required (2026-09-29): staff need it to price shipping on
        // special-order parts. US 5-digit / ZIP+4, or a Canadian postal code.
        const zip = get('zip');
        if (!/^(\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d)$/.test(zip)) {
          this.error = zip ? 'Please enter a valid ZIP code.' : 'Please enter your ZIP code — we need it to calculate shipping.';
          this.render();
          return;
        }
        this.error = null;
        this.submit({
          name,
          email,
          company: get('company'),
          phone: get('phone'),
          note: get('note'),
          address: get('address'),
          city: get('city'),
          state: get('state'),
          zip,
          country: get('country'),
        });
      });
    }
  }

  customElements.define('quote-request', QuoteRequest);
}
